import {
  BRAND_COLOR_ROLES,
  type BrandColorRole,
  type BrandTextStyleKey,
  type BrandTokens,
  DEFAULT_BRAND_TOKENS,
} from "../brand/tokens";
import { closestWeight } from "../scene/fonts";

/**
 * Design styles: a colour palette plus a font pairing. They are the look of
 * the template library while an organization has no brand kit, and the
 * starting point when it creates one (docs/06-brand-kit.md).
 */

interface StyleFont {
  fontFamily: string;
  fontWeight: number;
  italic?: boolean;
  letterSpacing?: number;
  uppercase?: boolean;
}

export interface BrandStyle {
  id: string;
  label: string;
  description: string;
  colors: Record<BrandColorRole, string>;
  /** Headings (heading-1, heading-2). */
  heading: StyleFont;
  /** Sublines. Defaults to the heading font at a lighter weight. */
  subline?: StyleFont;
  /** Body text and captions. */
  body: StyleFont;
  quote: StyleFont;
  radius?: BrandTokens["radius"];
}

export const BRAND_STYLES: readonly BrandStyle[] = [
  {
    id: "klar",
    label: "Klar & modern",
    description: "Sachlich, viel Weißraum, kräftiges Blau",
    colors: {
      primary: "#2563eb",
      secondary: "#0f172a",
      accent: "#f59e0b",
      background: "#ffffff",
      surface: "#eef2f7",
      text: "#0f172a",
      "text-muted": "#64748b",
    },
    heading: { fontFamily: "Inter", fontWeight: 800, letterSpacing: -0.02 },
    body: { fontFamily: "Inter", fontWeight: 400 },
    quote: { fontFamily: "Playfair Display", fontWeight: 500, italic: true },
  },
  {
    id: "warm",
    label: "Warm & freundlich",
    description: "Koralle, Sonnengelb und Creme, runde Schriften",
    colors: {
      primary: "#e4572e",
      secondary: "#3a2536",
      accent: "#ffc857",
      background: "#fff8f0",
      surface: "#fde6d4",
      text: "#3a2536",
      "text-muted": "#7d6570",
    },
    heading: { fontFamily: "Poppins", fontWeight: 700, letterSpacing: -0.01 },
    body: { fontFamily: "Nunito", fontWeight: 400 },
    quote: { fontFamily: "Caveat", fontWeight: 700 },
    radius: { sm: 12, md: 24, lg: 48 },
  },
  {
    id: "elegant",
    label: "Elegant",
    description: "Nachtblau und Gold, Serifen-Überschriften",
    colors: {
      primary: "#1f3a5f",
      secondary: "#0e1726",
      accent: "#c9a227",
      background: "#faf7f2",
      surface: "#eee7da",
      text: "#1c1c1c",
      "text-muted": "#6b665e",
    },
    heading: { fontFamily: "Playfair Display", fontWeight: 700, letterSpacing: -0.01 },
    subline: { fontFamily: "Lato", fontWeight: 700, letterSpacing: 0.04 },
    body: { fontFamily: "Lato", fontWeight: 400 },
    quote: { fontFamily: "Playfair Display", fontWeight: 400, italic: true },
    radius: { sm: 2, md: 4, lg: 8 },
  },
  {
    id: "laut",
    label: "Kräftig & laut",
    description: "Schwarz, Signalorange und Gelb, hohe Versalien",
    colors: {
      primary: "#ff4d00",
      secondary: "#111111",
      accent: "#ffd60a",
      background: "#ffffff",
      surface: "#f1f1f1",
      text: "#111111",
      "text-muted": "#555555",
    },
    heading: { fontFamily: "Bebas Neue", fontWeight: 400, letterSpacing: 0.01, uppercase: true },
    subline: { fontFamily: "Montserrat", fontWeight: 800, uppercase: true, letterSpacing: 0.02 },
    body: { fontFamily: "Montserrat", fontWeight: 500 },
    quote: { fontFamily: "Montserrat", fontWeight: 700, italic: true },
    radius: { sm: 0, md: 0, lg: 0 },
  },
  {
    id: "natur",
    label: "Natürlich",
    description: "Waldgrün, Ocker und Naturweiß",
    colors: {
      primary: "#2f6b4f",
      secondary: "#1d3a2b",
      accent: "#e3a03a",
      background: "#f7f5ee",
      surface: "#e4e8da",
      text: "#1f2a24",
      "text-muted": "#5e6a62",
    },
    heading: { fontFamily: "Lora", fontWeight: 700 },
    subline: { fontFamily: "Open Sans", fontWeight: 700 },
    body: { fontFamily: "Open Sans", fontWeight: 400 },
    quote: { fontFamily: "Lora", fontWeight: 500, italic: true },
    radius: { sm: 10, md: 20, lg: 40 },
  },
  {
    id: "verspielt",
    label: "Frisch & verspielt",
    description: "Violett und Cyan, eigenwillige Groteske",
    colors: {
      primary: "#7c3aed",
      secondary: "#1e1b4b",
      accent: "#22d3ee",
      background: "#ffffff",
      surface: "#f1edff",
      text: "#1e1b4b",
      "text-muted": "#6b6a8a",
    },
    heading: { fontFamily: "Space Grotesk", fontWeight: 700, letterSpacing: -0.02 },
    body: { fontFamily: "DM Sans", fontWeight: 400 },
    quote: { fontFamily: "DM Sans", fontWeight: 500, italic: true },
    radius: { sm: 12, md: 28, lg: 56 },
  },
  {
    id: "nacht",
    label: "Dunkel & neon",
    description: "Dunkler Grund, leuchtendes Cyan und Limette",
    colors: {
      primary: "#38bdf8",
      secondary: "#1e293b",
      accent: "#a3e635",
      background: "#0b1120",
      surface: "#162033",
      text: "#f8fafc",
      "text-muted": "#94a3b8",
    },
    heading: { fontFamily: "Montserrat", fontWeight: 800, letterSpacing: -0.01 },
    body: { fontFamily: "Inter", fontWeight: 400 },
    quote: { fontFamily: "Montserrat", fontWeight: 500, italic: true },
    radius: { sm: 8, md: 16, lg: 32 },
  },
  {
    id: "traditionell",
    label: "Traditionell",
    description: "Bordeaux und Sand, klassische Serifen – für Vereine und Handwerk",
    colors: {
      primary: "#8b1e3f",
      secondary: "#2b2b2b",
      accent: "#d4a373",
      background: "#ffffff",
      surface: "#f5eee8",
      text: "#2b2b2b",
      "text-muted": "#6d6660",
    },
    heading: { fontFamily: "Merriweather", fontWeight: 900 },
    subline: { fontFamily: "Source Serif 4", fontWeight: 700 },
    body: { fontFamily: "Source Serif 4", fontWeight: 400 },
    quote: { fontFamily: "Merriweather", fontWeight: 400, italic: true },
    radius: { sm: 4, md: 8, lg: 16 },
  },
];

export function getBrandStyle(id: string): BrandStyle | undefined {
  return BRAND_STYLES.find((style) => style.id === id);
}

/** Brand tokens of a style: the default kit with the style's colours, fonts and radii. */
export function brandStyleTokens(style: BrandStyle): BrandTokens {
  const fontFor: Record<BrandTextStyleKey, StyleFont> = {
    "heading-1": style.heading,
    "heading-2": style.heading,
    subline: style.subline ?? {
      ...style.heading,
      fontWeight: Math.min(style.heading.fontWeight, 600),
    },
    body: style.body,
    caption: { ...style.body, fontWeight: Math.max(style.body.fontWeight, 500) },
    quote: style.quote,
  };
  return {
    ...DEFAULT_BRAND_TOKENS,
    colors: DEFAULT_BRAND_TOKENS.colors.map((color) =>
      (BRAND_COLOR_ROLES as readonly string[]).includes(color.key)
        ? { ...color, color: style.colors[color.key as BrandColorRole] }
        : color,
    ),
    textStyles: DEFAULT_BRAND_TOKENS.textStyles.map((textStyle) => {
      const font = fontFor[textStyle.key as BrandTextStyleKey];
      if (!font) return textStyle;
      return {
        ...textStyle,
        fontFamily: font.fontFamily,
        fontWeight: closestWeight(font.fontFamily, font.fontWeight),
        italic: font.italic ?? false,
        letterSpacing: font.letterSpacing ?? textStyle.letterSpacing,
        uppercase: font.uppercase ?? false,
      };
    }),
    radius: style.radius ?? DEFAULT_BRAND_TOKENS.radius,
  };
}
